import { inject, Injectable, Injector } from '@angular/core';
import { environment } from "../../../../../environments/environment";
import { HttpClient, HttpParams } from "@angular/common/http";
import { catchError, filter, map, Observable, of, take } from "rxjs";
import {
  AddWalletRequest,
  CreateReferralLinkRequest,
  InvoiceDto,
  LatestAmlReport,
  Operation,
  PaginationResponse,
  PlanCost,
  PlanSuggestion,
  ProviderOutgoings,
  ProviderReport,
  Referral,
  RegisterUserForm,
  Rental,
  RentResourcesRequest,
  Report,
  ReportData,
  StopWalletTransaction,
  TransactionDto,
  AmlScanEntity,
  AccountDto,
  Wallet,
  WalletCheckNetworkDto,
  WalletCheckQueueDto,
  WalletEntity,
  WalletWithRentalDto,
  AmlScanQueryParams,
  PaginationQueryParams,
  AccountType,
  SupportedNetwork,
  WalletCheckRequest,
  CreateSubscriptionRequest,
  UpdateWallet,
  ReportProcess,
  ReportsList,
  ReportsStats,
  WalletsStats,
  WalletTransactionStats,
  ProjectSetting,
  PlanTemplateDto,
  UserPlanType,
  BannerDto,
  SurveyDto,
  SurveyAnswerDto,
  SurveyCompleteResponseDto,
  LoginHistoryEntry,
  LoginHistoryQueryParams,
  ActiveSession,
  TeamMemberSessions,
  CompanySessionsQueryParams,
  CompanyMemberDto
} from "../models/fraud.models";
import { MockFraudService } from "./mock-fraud.service";
import { parseTransactionData } from '../../partials/helpers/transaction-parser.helper';
import { AuthService } from '../../partials/services/auth.service';
import { normalizeSurveyDto } from '../../partials/utils/survey.utils';

interface ReqReportScanWalletBody {
  wallet: string;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private _apiUrl: string = environment.apiUrl;
  private _httpClient: HttpClient = inject(HttpClient);
  private _mockFraud: MockFraudService = inject(MockFraudService);
  private _injector: Injector = inject(Injector);

  get isMockData(): boolean {
    return environment.mockData;
  }

  reportScanWallet(body: ReqReportScanWalletBody): Observable<void> {
    return this._httpClient.post<void>(`${ this._apiUrl }/api/v1/reports/trial`, body);
  }

  // Operations
  getOperations(): Observable<PaginationResponse<Operation>> {
    return this._httpClient.get<PaginationResponse<Operation>>(`${ this._apiUrl }/api/operations`);
  }

  // Public
  handleProcessingCallback(body: TransactionDto): Observable<boolean> {
    return this._httpClient.post<boolean>(`${ this._apiUrl }/api/public/processing/callback`, body);
  }

  // Referrals
  getReferrals(): Observable<Referral[]> {
    return this._httpClient.get<Referral[]>(`${ this._apiUrl }/api/referrals`);
  }

  createReferral(body: CreateReferralLinkRequest): Observable<Referral> {
    return this._httpClient.post<Referral>(`${ this._apiUrl }/api/referrals`, body);
  }

  // Rental
  deleteRental(uuid: string): Observable<void> {
    return this._httpClient.delete<void>(`${ this._apiUrl }/api/rental/${uuid}`);
  }

  cancelInvoice(id: string): Observable<any> {
    return this._httpClient.delete<any>(`${ this._apiUrl }/api/invoices/cancel/${ id }`);
  }

  getPendingRental(uuid: string): Observable<Rental | undefined> {
    return this._httpClient.get<Rental | undefined>(`${ this._apiUrl }/api/rental/pending/wallet/${uuid}`)
  }

  validWallet(address: string): Observable<boolean> {
    let params = new HttpParams();
    params = params.append('address', address.toString());
    return this._httpClient.get<boolean>(`${ this._apiUrl }/api/rental/valid/${address}`);
  }

  calculateRentalPlanForWallet(address: string): Observable<PlanSuggestion> {
    if (this.isMockData) {
      return this._mockFraud.calculateRentalPlanForWallet(address);
    }
    return this._httpClient.get<PlanSuggestion>(`${ this._apiUrl }/api/rental/${ address }`);
  }

  calculatePlanCostForWallet(params: { incoming: number, outcoming: number, days?: number }): Observable<PlanCost> {
    return this._httpClient.get<PlanCost>(`${ this._apiUrl }/api/rental/plan-cost`, { params });
  }

  rentResourcesForWallet(body: RentResourcesRequest): Observable<{id: string}> {
    return this._httpClient.post<{id: string}>(`${ this._apiUrl }/api/rental`, body);
  }

  startTrial(address: string): Observable<void> {
    return this._httpClient.post<void>(`${ this._apiUrl }/api/rental/startTrial/${ address }`, {});
  }

  activeRentalPlan(uuid: string): Observable<void> {
    return this._httpClient.post<void>(`${ this._apiUrl }/api/rental/${uuid}/activate`, {});
  }

  // Transaction operations
  getTransactionByUuid(uuid: string): Observable<AmlScanEntity<LatestAmlReport>> {
    return this._httpClient.get<AmlScanEntity>(`${ this._apiUrl }/api/aml-scans/${ uuid }`).pipe(
      filter(Boolean),
      map((transaction) => {
        return parseTransactionData(transaction)
      }),
    )
  }

  getDepositAddress(): Observable<string> {
    return this._httpClient.get(`${ this._apiUrl }/api/v1/users/me/deposit-address`, { responseType: 'text' });
  }

  register(body: RegisterUserForm): Observable<AccountDto> {
    return this._httpClient.post<AccountDto>(`${ this._apiUrl }/api/v1/users/register`, body);
  }

  getUserByInitData(): Observable<AccountDto> {
    return this._httpClient.get<AccountDto>(`${ this._apiUrl }/api/v1/users/me`);
  }

  // Wallet
  getSubscribedWallets(): Observable<WalletWithRentalDto[]> {
    let params = new HttpParams();
    // params = params.append('walletType', 'AML_ENERGY');
    return this._httpClient.get<WalletWithRentalDto[]>(`${ this._apiUrl }/api/v1/wallet`, {params});
  }

  getWalletsV2(value: Partial<PaginationQueryParams> & { filter?: 'all' | 'active' | 'suspend' | 'canceled' }): Observable<PaginationResponse<WalletWithRentalDto>> {
    const { filter, ...rest } = value;
    let params = new HttpParams({
      fromObject: { ...rest }
    });
    if (filter && filter !== 'all') {
      const statusMap: Record<'active' | 'suspend' | 'canceled', 'ACTIVE' | 'SUSPENDED' | 'DISABLED'> = {
        active: 'ACTIVE',
        suspend: 'SUSPENDED',
        canceled: 'DISABLED',
      };
      params = params.append('subscriptionStatus', statusMap[filter]);
    }
    return this._httpClient.get<PaginationResponse<WalletWithRentalDto>>(`${ this._apiUrl }/api/v2/wallet`, {params});
  }

  getWalletsStatsV2(): Observable<WalletsStats> {
    return this._httpClient.get<WalletsStats>(`${ this._apiUrl }/api/v2/wallet/stats`);
  }

  addWallet(body: AddWalletRequest, forceCreation?: boolean): Observable<WalletEntity> {
    let params = new HttpParams();
    forceCreation && (params = params.append('forceCreation', forceCreation.toString()));
    return this._httpClient.post<WalletEntity>(`${ this._apiUrl }/api/v1/wallet`, body);
  }

  getWallet(uuid: string): Observable<Wallet> {
    return this._httpClient.get<Wallet>(`${ this._apiUrl }/api/v1/wallet/${ uuid }`);
  }

  patchUpdateWallet(uuid: string, body: Partial<Pick<Wallet, 'address' | 'name'>>): Observable<Wallet> {
    return this._httpClient.put<Wallet>(`${ this._apiUrl }/api/v1/wallet/${ uuid }`, body);
  }

  getInfoWallet(address: string): Observable<Wallet> {
    return this._httpClient.get<Wallet>(`${ this._apiUrl }/api/v1/wallet/address/${ address }`);
  }

  deleteWallet(uuid: string): Observable<void> {
    return this._httpClient.delete<void>(`${ this._apiUrl }/api/v1/wallet/${ uuid }`);
  }

  getLatestReportById(id: number): Observable<(Report<ReportData> & { providerReports: ProviderReport<ProviderOutgoings>[] }) | undefined> {
    return this._httpClient.get<Report<string> & { providerReports?: ProviderReport<string>[] }>(`${ this._apiUrl }/api/v1/reports/${ id }`).pipe(map((report) => {
      const { providerReports: _, ...reportWithoutProviderReports } = report;
      let parsedData: ReportData | null;

      // Проверяем старый формат: если data !== null, парсим её
      if (report.data === null) {
        parsedData = null;
      } else if (report.data !== undefined && report.data !== '') {
        try {
          parsedData = JSON.parse(report.data);
        } catch (e) {
          parsedData = null;
        }
      } else {
        parsedData = null;
      }

      // Проверяем новый формат: если есть providerReports, перебираем и парсим data в каждом элементе
      let parsedProviderReports: ProviderReport<ProviderOutgoings>[] = [];
      if (report.providerReports && Array.isArray(report.providerReports)) {
        parsedProviderReports = report.providerReports.map((providerReport: ProviderReport<string>): ProviderReport<ProviderOutgoings> => ({
          ...providerReport,
          data: providerReport.status === 'COMPLETED'
            ? JSON.parse(providerReport.data ?? '') as ProviderOutgoings
            : (providerReport.data as unknown as ProviderOutgoings)
        }));
      }

      const result: Report<ReportData> & { providerReports: ProviderReport<ProviderOutgoings>[] } = {
        ...reportWithoutProviderReports,
        data: parsedData as ReportData,
        providerReports: parsedProviderReports
      } as Report<ReportData> & { providerReports: ProviderReport<ProviderOutgoings>[] };
      return result;
    }));
  }

  getRentalWallet(uuid: string): Observable<Rental[]> {
    return this._httpClient.get<Rental[]>(`${ this._apiUrl }/api/rental/wallet/${ uuid }`);
  }

  getInvoice(id: string): Observable<InvoiceDto> {
    return this._httpClient.get<InvoiceDto>(`${ this._apiUrl }/api/invoices/${ id }`);
  }

  // Wallet Check
  getCheckWalletsNetworks(): Observable<WalletCheckNetworkDto[]> {
    return this._httpClient.get<WalletCheckNetworkDto[]>(`${ this._apiUrl }/api/v1/wallet-check/networks`);
  }

  getCheckWalletsPending(): Observable<WalletCheckQueueDto> {
    return this._httpClient.get<WalletCheckQueueDto>(`${ this._apiUrl }/api/v1/wallet-check/pending`);
  }

  getCheckWalletsById(id: number): Observable<WalletCheckQueueDto> {
    return this._httpClient.get<WalletCheckQueueDto>(`${ this._apiUrl }/api/v1/wallet-check/${id}`);
  }

  postCheckWallet(body: {address: string, network: string, reportProcess: ReportProcess}): Observable<WalletCheckQueueDto> {
    return this._httpClient.post<WalletCheckQueueDto>(`${ this._apiUrl }/api/v1/wallet-check`, body)
  }

  postCheckWalletValidate(body: {address: string, network: string}): Observable<{smartContract: boolean}> {
    return this._httpClient.post<{smartContract: boolean}>(`${ this._apiUrl }/api/v1/wallet-check/validate`, body);
  }

  deleteCheckWalletPending(): Observable<void> {
    return this._httpClient.delete<void>(`${ this._apiUrl }/api/v1/wallet-check/pending`);
  }

  getWalletBalanceForSupportedTokens(address: string, network: string): Observable<{ [key: string]: number }> {
    return this._httpClient.get<{
      [key: string]: number
    }>(`${ this._apiUrl }/api/v1/subscriptions/wallet-balance/${ network }/${ address }`);
  }

  getAmlScanByUuid(uuid: string): Observable<AmlScanEntity<LatestAmlReport>> {
    return this._httpClient.get<AmlScanEntity>(`${ this._apiUrl }/api/aml-scans/${ uuid }`).pipe(
      filter(Boolean),
      map((transaction) => {
        const latestAmlReport = JSON.parse(transaction.latestAmlReport ?? '') as LatestAmlReport;
        return {
          ...transaction,
          latestAmlReport
        }
      }),
    );
  }

  putSubmitFreezeRequest(caseId: string, amlScanUuid: string, body: {comment: string}): Observable<any> {
    return this._httpClient.put(`${this._apiUrl}/api/cases/${caseId}/aml-scan/${amlScanUuid}/submit-freeze-request`, body);
  }

  postStopWalletTransactionId(transitionId: string, body: { blockType: string, blockedAmount: string, blockExpireAt: Date }): Observable<StopWalletTransaction> {
    return this._httpClient.post<StopWalletTransaction>(`${this._apiUrl}/api/compliance/cluster-transaction/${transitionId}/block`, body);
  }

  getAmlScanList(value: Partial<AmlScanQueryParams>): Observable<PaginationResponse<AmlScanEntity>> {
    let params = new HttpParams();

    Object.keys(value).forEach(key => {
      const paramValue = value[key as keyof AmlScanQueryParams];
      if (paramValue !== undefined && paramValue !== null) {
        params = params.set(key, paramValue.toString());
      }
    });

    params = params.append('sourceType', 'API,AMLEnergy,BOT,MONITOR');

    return this._httpClient.get<PaginationResponse<AmlScanEntity>>(`${this._apiUrl}/api/aml-scans`, {
      params
    });
  }
  
  getListReports(value: Partial<PaginationQueryParams> & { walletUuid?: string }): Observable<PaginationResponse<ReportsList>> {
    const params = new HttpParams({
      fromObject: { ...value, sort: 'createdAt,desc' }
    });
    return this._httpClient.get<PaginationResponse<ReportsList>>(`${this._apiUrl}/api/v1/unified-activity`, { params });
  }

  getListReportsStats(walletUuid?: string): Observable<ReportsStats> {
    let params = new HttpParams();
    if (walletUuid) {
      params = params.append('walletUuid', walletUuid);
    }
    return this._httpClient.get<ReportsStats>(`${this._apiUrl}/api/v1/unified-activity/stats`, { params });
  }

  getAccounts(): Observable<AccountDto[]> {
    return this._httpClient.get<AccountDto[]>(`${this._apiUrl}/api/v1/users/accounts`);
  }

  putActiveTelegramAccount(uuid: string): Observable<never> {
    return this._httpClient.put<never>(`${ this._apiUrl }/api/v1/users/me/active-telegram-account/${uuid}`, {});
  }

  sendRegistrationInstructions(accountId?: string): Observable<never> {
    const authService = this._injector.get(AuthService);
    const accounts = authService.accounts();
    
    let personalAccountUuid: string | undefined;
    if (accounts) {
      const personalAccount = accounts.find(account => account.accountType === AccountType.PERSONAL);
      personalAccountUuid = personalAccount?.uuid;
    }
    
    const headers: { [key: string]: string } = {};
    const accountIdToUse = accountId || personalAccountUuid;
    if (accountIdToUse) {
      headers['x-account-Id'] = accountIdToUse;
    }
    
    return this._httpClient.post<never>(`${ this._apiUrl }/api/v1/users/send-finish-business-setup-notification`, {}, {
      headers
    });
  }

  getSupportedNetworks(): Observable<SupportedNetwork[]> {
    return this._httpClient.get<SupportedNetwork[]>(`${this._apiUrl}/api/v1/subscriptions/supported-networks`);
  }

  getWalletValidAllWalletType(address: string, network: string, walletType?: 'AML_ENERGY' | 'USER_PLAN'): Observable<WalletWithRentalDto[]> {
    let params = new HttpParams();
    walletType && (params = params.append('walletType', walletType));
    return this._httpClient.get<WalletWithRentalDto[]>(`${this._apiUrl}/api/v1/wallet/${network}/${address}`, { params });
  }

  postValidateWallet(body: WalletCheckRequest, queryParams: Partial<{
    doActivenessCheck: boolean;
    doRestrictSmartContracts: boolean
  }> = {}): Observable<void> {
    const params = new HttpParams({ fromObject: queryParams })
    return this._httpClient.post<void>(`${this._apiUrl}/api/v1/wallet-check/validate`, body, { params });
  }

  getWalletValidWalletType(address: string, network: string, walletType: 'AML_ENERGY' | 'USER_PLAN'): Observable<WalletWithRentalDto> {
    return this._httpClient.get<WalletWithRentalDto>(`${this._apiUrl}/api/v1/wallet/${walletType}/${network}/${address}`);
  }

  createWalletSubscription(body: CreateSubscriptionRequest): Observable<Wallet> {
    return this._httpClient.post<Wallet>(`${this._apiUrl}/api/v1/subscriptions/wallet`, body);
  }

  updateWallet(uuid: string, body: Partial<UpdateWallet>): Observable<Wallet> {
    return this._httpClient.put<Wallet>(`${this._apiUrl}/api/v1/wallet/${uuid}`, body);
  }

  disableWalletSubscription(walletUuid: string): Observable<Wallet> {
    return this._httpClient.put<Wallet>(`${this._apiUrl}/api/v1/subscriptions/wallet/${walletUuid}/disable`, {});
  }

  putActivateWalletSubscription(walletUuid: string): Observable<Wallet> {
    return this._httpClient.put<Wallet>(`${this._apiUrl}/api/v1/subscriptions/wallet/${walletUuid}/activate`, {});
  }

  patchWalletSubscriptions(subscriptionId: number, form: { directions?: { INCOMING: string[], OUTGOING: string[] }, status?: string }): Observable<Wallet> {
    return this._httpClient.put<Wallet>(`${this._apiUrl}/api/v1/subscriptions/${subscriptionId}`, form);
  }

  getWalletTransactionStats(body: {address: string, network: string, tokens: string[]}): Observable<WalletTransactionStats> {
    const params = new HttpParams({
      fromObject: {
        address: body.address,
        network: body.network,
        tokens: body.tokens.join(','),
      },
    });
    return this._httpClient.get<WalletTransactionStats>(`${this._apiUrl}/api/v1/wallet/transaction-stats`, { params });
  }
  
  getProjectSetting(): Observable<ProjectSetting> {
    return this._httpClient.get<ProjectSetting>(`${this._apiUrl}/api/v1/project-settings`);
  }

  postWelcomePageViewed(): Observable<void> {
    return this._httpClient.post<void>(`${ this._apiUrl }/api/v1/users/welcome-page-viewed`, {});
  }

  getPlanTemplates(): Observable<PlanTemplateDto[]> {
    return this._httpClient.get<PlanTemplateDto[]>(`${this._apiUrl}/api/v1/plan-templates`);
  }

  postUserPlanUpgrade(body: { type: UserPlanType, billingCycle: 'MONTHLY' | 'YEARLY' | 'CUSTOM' }): Observable<any> {
    return this._httpClient.post<any>(`${ this._apiUrl }/api/v1/user-plans/upgrade`, body);
  }

  postUserPlanPurchase(body: { type: UserPlanType, billingCycle: 'MONTHLY' | 'YEARLY' | 'CUSTOM' }): Observable<{invoiceUuid: string, userPlanStatus: string}> {
    return this._httpClient.post<any>(`${ this._apiUrl }/api/v1/user-plans/purchase`, body);
  }

  getBanners(): Observable<BannerDto[]> {
    return this._httpClient.get<BannerDto[]>(`${this._apiUrl}/api/v1/banners`);
  }

  postDismissBanner(id: number): Observable<any> {
    return this._httpClient.post<any>(`${ this._apiUrl }/api/v1/banners/${id}/dismiss`, {});
  }

  getSurvey(surveyId: number): Observable<SurveyDto> {
    return this._httpClient
      .get<SurveyDto>(`${this._apiUrl}/api/v1/surveys/${surveyId}`)
      .pipe(map((survey) => normalizeSurveyDto(survey)));
  }

  postSurveyAnswers(surveyId: number, body: { answers: SurveyAnswerDto[] }): Observable<void> {
    return this._httpClient.post<void>(`${this._apiUrl}/api/v1/surveys/${surveyId}/answers`, body);
  }

  completeSurvey(surveyId: number): Observable<SurveyCompleteResponseDto> {
    return this._httpClient.post<SurveyCompleteResponseDto>(`${this._apiUrl}/api/v1/surveys/${surveyId}/complete`, {});
  }

  postRenewUserPlan(body: { type: UserPlanType, billingCycle: 'MONTHLY' | 'YEARLY' | 'CUSTOM' }): Observable<any> {
    return this._httpClient.post<any>(`${ this._apiUrl }/api/v1/user-plans/renew`, body);
  }

  getSupportedNetworksAll(): Observable<SupportedNetwork[]> {
    return this._httpClient.get<SupportedNetwork[]>(`${this._apiUrl}/api/v1/subscriptions/supported-networks/all`);
  }

  // Login history & sessions
  getLoginHistory(query: LoginHistoryQueryParams = {}): Observable<PaginationResponse<LoginHistoryEntry>> {
    const params = buildQueryParams(query);
    return this._httpClient.get<PaginationResponse<LoginHistoryEntry>>(`${this._apiUrl}/api/v1/users/me/logins`, { params });
  }

  getActiveSessions(): Observable<ActiveSession[]> {
    return this._httpClient.get<ActiveSession[]>(`${this._apiUrl}/api/v1/users/me/sessions`);
  }

  deleteSession(id: string): Observable<void> {
    return this._httpClient.delete<void>(`${this._apiUrl}/api/v1/users/me/sessions/${id}`);
  }

  getCompanyLogins(query: LoginHistoryQueryParams = {}): Observable<PaginationResponse<LoginHistoryEntry>> {
    const params = buildQueryParams(query);
    return this._httpClient.get<PaginationResponse<LoginHistoryEntry>>(`${this._apiUrl}/api/v1/companies/me/logins`, { params });
  }

  getCompanySessions(query: CompanySessionsQueryParams = {}): Observable<PaginationResponse<TeamMemberSessions>> {
    const params = buildQueryParams(query);
    return this._httpClient.get<PaginationResponse<TeamMemberSessions>>(`${this._apiUrl}/api/v1/companies/me/sessions`, { params });
  }

  deleteCompanyMemberSession(userUuid: string, id: string): Observable<void> {
    return this._httpClient.delete<void>(`${this._apiUrl}/api/v1/companies/me/members/${userUuid}/sessions/${id}`);
  }

  getCompanyMembers(query: Partial<PaginationQueryParams> = {}): Observable<PaginationResponse<CompanyMemberDto>> {
    const params = buildQueryParams(query);
    return this._httpClient.get<PaginationResponse<CompanyMemberDto>>(`${this._apiUrl}/api/v1/companies/members`, { params });
  }

  postUsersLogout(): Observable<void> {
    return this._httpClient.post<void>(`${this._apiUrl}/api/v1/users/logout`, {});
  }
}

function buildQueryParams<T extends object>(query: T): HttpParams {
  let params = new HttpParams();
  (Object.keys(query) as (keyof T)[]).forEach((key) => {
    const value = query[key] as unknown;
    if (value !== undefined && value !== null && value !== '') {
      params = params.set(key as string, String(value));
    }
  });
  return params;
}
